import { Schema } from "effect"
import { EntityRef, NodeRef, Principal } from "./common"
import { WorkRequest } from "./coordination"
import { Monitor } from "./domain/agentlife"
import { BriefSections } from "./domain/brief-sections"
import { Edge, Item, ItemStatus } from "./domain/item"
import { Artifact, Comment, MemoryItem } from "./domain/knowledge"
import { Agent, Area, Policy } from "./domain/org"
import { Goal, Kpi } from "./domain/plan"
import { Proposal } from "./domain/proposal"
import { Thread } from "./domain/thread"
import { Member, Project } from "./domain/workspace"
import { AgentId, Epoch, ItemId, Millis, NonNegInt, ProposalId, RunId, RunnerId, Seq, UserId } from "./ids"
import { AskWait, Unmet } from "./placement"
import { RunViewPhase } from "./rpc/run"
import { ActivityUsage, Run } from "./run"

// #region project-state
/**
 * The decision state of one project: canonical in memory for `decide`, encoded as the snapshot clients decode,
 * and rebuilt by folding the log from the nearest snapshot. History (resolved proposals beyond a window, ended
 * runs, comment bodies, KPI series, memory history) is served from SQL projections, not kept here.
 * `v: 2` (decision 0036): a stored snapshot without `v` is a v1 snapshot, decoded with the frozen v1 schema and upcast.
 */
export class ProjectState extends Schema.Opaque<ProjectState>()(
  Schema.Struct({
    v: Schema.Literal(2),
    seq: Seq,
    planVersion: Schema.Int,
    project: Schema.NullOr(Project), // null before genesis
    members: Schema.Array(Member),
    mayor: Schema.NullOr(Schema.Struct({ agentId: AgentId, ownerId: UserId })),
    policies: Schema.Array(Policy),
    areas: Schema.Array(Area),
    agents: Schema.Array(Agent), // roster: lead + role agents
    goals: Schema.Array(Goal),
    kpis: Schema.Array(Kpi),
    /** Every item, including closed ones (they are part of the plan); old cancelled intake drops after 7 days. */
    items: Schema.Array(Item),
    edges: Schema.Array(Edge),
    /** Open proposals plus those resolved within the compaction window (ghosts fade out on the canvas). */
    proposals: Schema.Array(Proposal),
    /** Runs not yet ended, plus the latest run per item and per agent. History reads the runs projection. */
    runs: Schema.Array(Run),
    /** Open asks only (bodies included); other comments are fetched per thread. */
    openAsks: Schema.Array(Comment),
    artifacts: Schema.Array(Artifact), // headers
    memory: Schema.Array(MemoryItem), // accepted project, area and item items
    /** Per-kind counters for short refs. Assigned in decide, carried in the event. */
    refCounters: Schema.Struct({
      G: Schema.Int,
      K: Schema.Int,
      /**
       * Old initiatives (I-n) and tasks (T-n): read-only after the cutover; new item numbers continue after them.
       * Decision 0142: thread refs (T-n) continue after them too (see `threads`).
       */
      I: Schema.Int,
      T: Schema.Int,
      P: Schema.Int,
      R: Schema.Int,
      /** Temporary teammates (settings wave §5.3): W-n workers, V-n validators. Absent = 0. */
      W: Schema.optionalKey(Schema.Int),
      V: Schema.optionalKey(Schema.Int),
      /** Item refs `<project key>-n` (decision 0035). Absent = 0. */
      items: Schema.optionalKey(Schema.Int),
      /** Output refs O-n (wave K). Absent = not numbered yet (see core `numberOutputs`). */
      O: Schema.optionalKey(Schema.Int),
      /** Memory refs M-n (wave C, decision 0071). Absent = 0 (see core `numberMemory`). */
      M: Schema.optionalKey(Schema.Int),
      /** Decision refs D-n (decision 0144): decided cards and decision comments. Absent = 0. */
      D: Schema.optionalKey(Schema.Int),
      /**
       * Decision 0142: thread refs read `T-n` and continue after the old tasks (`T`), never meeting one; their own
       * counter, so item numbers (which skip old T numbers) don't move. Absent = 0.
       */
      threads: Schema.optionalKey(Schema.Int),
    }),
    // ---- wave C (decision 0071): the clock in the Project DO. Optional keys: absent = empty, no upcast. ----
    /** People's monitors (decision C2: one clock, in the Project DO). Absent = []. */
    monitors: Schema.optionalKey(Schema.Array(Monitor)),
    workRequests: Schema.optionalKey(Schema.Array(WorkRequest)),
    /**
     * Re-walk 2026-10-28 (decision 0166 §2): messages waiting behind another turn whose own turn would wait on a login
     * (`AskWait`), one per agent and asker; cleared when that asker's turn is requested. Absent = none.
     */
    askWaits: Schema.optionalKey(Schema.Array(AskWait)),
    /**
     * The daily loose-ends check (A1): when it last wrote (`lastAt`), the loose-end keys open then (`keys`), and when it
     * last woke someone (`wokeAt`). Absent = never ran.
     */
    dailyCheck: Schema.optionalKey(
      Schema.Struct({
        lastAt: Millis,
        keys: Schema.Array(Schema.String),
        wokeAt: Schema.NullOr(Millis),
      }),
    ),
    /**
     * Timed turns per agent (keyed by AgentId) on `day` (yyyy-mm-dd in the project's zone): `count` monitor fires and
     * `series_due` wakes delivered; `extra` = a person pressed Continue on that day's cap card (no cap for the rest of
     * the day). A different `day` counts from 0. Absent = none today.
     */
    timedTurns: Schema.optionalKey(
      Schema.Record(
        Schema.String,
        Schema.Struct({ day: Schema.String, count: NonNegInt, extra: Schema.Boolean }),
      ),
    ),
    /** Per recurring series (keyed by ItemId): the last period (yyyy-mm-dd) noted as due (NoteSeriesDue). */
    seriesDue: Schema.optionalKey(Schema.Record(Schema.String, Schema.String)),
    /**
     * Decision 0098: each agent's newest brief to its keeper (keyed by AgentId; BriefSent). Absent = none sent. Older
     * briefs live in the log and the keeper's inbox.
     */
    briefs: Schema.optionalKey(
      Schema.Record(
        Schema.String,
        Schema.Struct({
          to: UserId,
          body: Schema.String,
          at: Millis,
          sections: Schema.optionalKey(BriefSections),
        }),
      ),
    ),
    /**
     * Decision 0130 (0126 §9): each agent's newest brief per person (agentId → userId → brief). `briefs` keeps the
     * newest to anyone (when the agent last briefed). Absent on states folded before 0130: `briefs[agent].to` stands in.
     */
    briefsTo: Schema.optionalKey(
      Schema.Record(
        Schema.String,
        Schema.Record(
          Schema.String,
          Schema.Struct({ body: Schema.String, at: Millis, sections: Schema.optionalKey(BriefSections) }),
        ),
      ),
    ),
    /**
     * Decision 0142: the project's threads (`T-n`), with who is in each and what external participants have not picked
     * up yet. Message bodies are comments (served from the comments projection). Absent = none.
     */
    threads: Schema.optionalKey(Schema.Array(Thread)),
  }),
) {}
// #endregion project-state

// #region views
/**
 * Why an item does (not) run: derived on server and client by the same core function (`readiness`, first match wins).
 * Never stored, never written by agents. `with_person` ("your turn") is never dispatched; it drives My work.
 */
export const Readiness = Schema.TaggedUnion({
  /** status ∉ {todo, in_progress}. */
  not_applicable: { status: ItemStatus },
  /** A series (recurrence set): never dispatched itself; Run now creates an occurrence (its child). */
  recurring: { openOccurrence: Schema.NullOr(ItemId) },
  running: {
    runId: RunId,
    state: Schema.Literals(["requested", "waiting", "leased"]),
    unmet: Schema.Array(Unmet),
  }, // "waiting for runner" lives here
  /** Settings wave K8: the project is paused or archived; no new worker attempts. */
  project_inactive: { status: Schema.Literals(["paused", "archived"]) },
  paused: {},
  /** A container with open children (progress open/total). */
  has_open_children: { open: NonNegInt, total: NonNegInt },
  /** A container whose children are all closed: its owner closes it. */
  ready_to_close: {},
  /** A plannable item with no open work under it: never dispatched, never "no owner". */
  empty: {},
  unassigned: { missing: Schema.Literals(["owner", "assignee"]) },
  owner_paused: { agentId: AgentId },
  /** Settings wave K10: the owner agent's human owner left the workspace; someone must take the agent over first. */
  owner_left: { agentId: AgentId },
  needs_checks: {}, // planner rule: no worker starts a leaf without ≥ 1 done check
  /** The top-level item above is not Now, or an item above is in triage/backlog/blocked/closed or paused. */
  not_now: {
    itemId: ItemId,
    why: Schema.Literals([
      "top_not_now",
      "ancestor_triage",
      "ancestor_backlog",
      "ancestor_blocked",
      "ancestor_closed",
      "ancestor_paused",
    ]),
  },
  waiting_request: {}, // a cross-area request the receiving area has not accepted yet
  waiting_until: { at: Millis }, // notBefore is in the future
  /** Unfinished `blocks` predecessors of it or an item above; cancelled ones (won't do, impossible) keep holding. */
  waiting_deps: { on: Schema.Array(NodeRef), cancelled: Schema.Array(ItemId) },
  /** Open proposals whose `blocks` name it or an item above (G2). */
  waiting_decision: { proposalIds: Schema.Array(ProposalId) },
  retry_backoff: { until: Millis },
  at_capacity: { agentId: AgentId, limit: Schema.Int }, // the owner's spawn_workers.maxConcurrent is reached
  /** A person's leaf with every gate open: "your turn". */
  with_person: { userId: UserId },
  ready: {},
})
export type Readiness = typeof Readiness.Type

/** Live agent state for avatars, rosters and canvas presence. Projection pushed by the Agent DO; never logged. */
export const AgentPresence = Schema.Struct({
  agentId: AgentId,
  state: Schema.Literals(["idle", "queued", "thinking", "working", "away", "paused", "needs_attention"]),
  reason: Schema.NullOr(Schema.String), // "vps-1 offline since 14:02", "claude profile logged out"
  currentRunId: Schema.NullOr(RunId),
  focus: Schema.Array(EntityRef), // what it is reading or editing right now
  activeWorkers: Schema.Int,
  queuedWakes: Schema.Int,
  lastActiveAt: Schema.NullOr(Millis),
})

/**
 * Ephemeral live activity of one run (fix wave §4.3, decision 0022): folded by the Run DO from the frames it stores
 * (core `foldActivity`), relayed by the Project DO in `SyncFrame.Presence.runs`. Never enters ProjectState, the log,
 * a pack or any agent tool. Carries no tool output and no reasoning content.
 */
export const RunActivity = Schema.Struct({
  runId: RunId,
  epoch: Epoch,
  /** Time of the newest content frame. */
  at: Millis,
  phase: RunViewPhase,
  /** From the last heartbeat: time since the CLI produced anything. */
  idleMs: NonNegInt,
  /** The effective model, once known. */
  model: Schema.NullOr(Schema.String),
  frames: NonNegInt,
  toolCalls: NonNegInt,
  last: Schema.NullOr(
    Schema.TaggedUnion({
      tool: {
        name: Schema.String,
        subject: Schema.NullOr(Schema.String),
        state: Schema.Literals(["running", "done", "error", "denied"]),
      },
      /** ≤ 140 chars, assistant text only. */
      text: { excerpt: Schema.String },
      /** No reasoning content, ever. */
      thinking: {},
      permission: { tool: Schema.String, title: Schema.String },
      /** ≤ 200 chars, from data-cli-error / turn.failed. */
      error: { message: Schema.String },
      milestone: { kind: Schema.String, text: Schema.String },
    }),
  ),
  usage: Schema.NullOr(ActivityUsage),
})
export type RunActivity = typeof RunActivity.Type

/** Ephemeral presence for people and agents, piggybacked on the project socket. */
export const PresenceEntry = Schema.Struct({ who: Principal, focus: Schema.Array(EntityRef), at: Millis })

/** Runner dot on the canvas and the Runners screen (derived from the hub's rows). */
export const RunnerStatus = Schema.Struct({
  runnerId: RunnerId,
  presence: Schema.Literals(["online", "busy", "away", "offline", "needs_attention"]),
  freeSlots: Schema.Int,
  leases: Schema.Array(Schema.Struct({ runId: RunId, epoch: Epoch, itemId: Schema.NullOr(ItemId) })),
  lastSeenAt: Schema.NullOr(Millis),
})
// #endregion views
